import React, { useCallback, useEffect, useState } from 'react';
import { Search, Plus, Phone, Building2, MessageCircle } from 'lucide-react';
import clsx from 'clsx';
import Avatar from './WaAvatar';
import Badge from './WaBadge';
import ContactFormModal from './ContactFormModal';
import { formatPhone } from '../../services/whatsapp/waFormatters';
import { api } from '../../services/api';

interface Contact {
  id: string;
  name: string;
  phone: string;
  company?: string;
  type: 'PF' | 'PJ';
  tags: string[];
}

interface ContactListProps {
  onStartConversation: (contact: Contact) => void;
}

export default function ContactList({ onStartConversation }: ContactListProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'PF' | 'PJ'>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { size: 50 };
      if (search) params.search = search;
      const resp = await api.get('/api/v1/empresas/contatos/todos/', { params });
      const payload = resp.data?.data;
      const items: any[] = payload?.items || [];
      setContacts(
        items.map(c => ({
          id: String(c.id),
          name: c.nome,
          phone: c.telefone || '',
          company: c.empresa_nome || undefined,
          type: c.empresa_nome ? ('PJ' as const) : ('PF' as const),
          tags: [c.departamento, c.cargo].filter(Boolean) as string[],
        })),
      );
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchContacts, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchContacts]);

  const filtered = contacts.filter(c => {
    if (typeFilter !== 'all' && c.type !== typeFilter) return false;
    return true;
  });

  const handleSaveContact = async () => {
    try {
      await fetchContacts();
    } catch {
      // erro tratado no modal
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="space-y-3 px-4 pb-2 pt-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight text-gray-800">Contatos</h2>
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-400">
            {filtered.length}
          </span>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, telefone ou empresa..."
            className="w-full rounded-full border-none bg-gray-100 py-2 pl-9 pr-4 text-sm outline-none transition-all placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-green-500/30"
          />
        </div>

        {/* Type filter */}
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
          {[
            { key: 'all' as const, label: 'Todos' },
            { key: 'PJ' as const, label: 'Empresas (PJ)' },
            { key: 'PF' as const, label: 'Pessoas (PF)' },
          ].map(tab => (
            <button
              type="button"
              key={tab.key}
              onClick={() => setTypeFilter(tab.key)}
              className={clsx(
                'flex-shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                typeFilter === tab.key
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <p className="text-sm">Carregando contatos...</p>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <p className="text-sm">Nenhum contato encontrado</p>
          </div>
        )}
        {!loading &&
          filtered.length > 0 &&
          filtered.map(contact => (
            <div
              key={contact.id}
              className="group flex items-center gap-3 border-b border-gray-100 px-4 py-3 transition-all hover:bg-gray-50"
            >
              <div className="relative flex-shrink-0">
                <Avatar name={contact.name} size="md" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-gray-900">{contact.name}</span>
                </div>
                {contact.company && (
                  <span className="mt-0.5 flex items-center gap-1 text-xs text-gray-400">
                    <Building2 size={10} />
                    {contact.company}
                  </span>
                )}
                <div className="mt-0.5 flex items-center gap-1">
                  <Phone size={10} className="text-gray-300" />
                  <span className="text-xs text-gray-400">{formatPhone(contact.phone)}</span>
                </div>
                {contact.tags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {contact.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                <Badge
                  text={contact.type}
                  variant={contact.type === 'PJ' ? 'info' : 'neutral'}
                  size="sm"
                />
                <button
                  type="button"
                  onClick={() => onStartConversation(contact)}
                  className="flex items-center gap-1 rounded-full bg-green-600 px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-all hover:bg-green-700 group-hover:opacity-100"
                >
                  <MessageCircle size={12} />
                  Conversar
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Add contact button */}
      <div className="border-t border-gray-100 p-3">
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium text-green-600 transition-all hover:bg-green-50"
        >
          <Plus size={16} />
          Adicionar Contato
        </button>
      </div>

      {/* Contact form modal */}
      <ContactFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveContact}
      />
    </div>
  );
}
